"use client";

import React from "react";
import styles from "./CoreCard.module.scss";
import Image from "@/components/common/Image";
import { Accordion } from "@/components/ui/Accordion";
import type { FeatureData as CoreData } from "@/services/featureListing";
import { Heading } from "../Heading";
import RichText from "@/components/common/RichText";
import { transformRichText } from "@/lib/richTextTransform";

interface CoreCardProps {
  core: CoreData;
  index?: number;
}

function titleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * CoreCard - Displays a single core option with:
 * - Image and bullets on the left
 * - Specification table on the right
 * - Accordion with Product Data Sheets at the bottom
 */
const CoreCard: React.FC<CoreCardProps> = ({ core, index = 0 }) => {
  const hasBullets = Array.isArray(core.bullets)
    ? core.bullets.length > 0
    : Boolean(core.bullets?.json);
  // Prepare accordion items for Product Data Sheets
  const pdfAccordionItems = core.productDataSheets?.length
    ? [
        {
          title: "Product Data Sheets",
          content: { json: undefined }, // We'll render links manually
          ctAsCollection: {
            items: core.productDataSheets.map((pdf) => ({
              sys: { id: `pdf-${pdf.title}` },
              title: titleCase(pdf.title),
              link: pdf.url,
              type: "link",
            })),
          },
          open: false,
        },
      ]
    : [];

  return (
    <article
      className={styles.coreCard}
      id={core.anchorId}
      data-core-index={index}
    >
      {/* Old links used #core-{title slug}; keep them landing here. */}
      {core.anchorId !== `core-${core.handle}` && (
        <span id={`core-${core.handle}`} className={styles.anchorAlias} />
      )}
      <div className={styles.coreCardInner}>
        {/* <h3 className={styles.coreTitle}>{core.title}</h3> */}
        <Heading level="h3" textSize="text-[3rem]" bottomBorder>
          {core.title}
        </Heading>
        {/* Two-column layout: Image/Bullets on left, Table on right */}
        <div className={`${styles.contentGrid} mt-8`}>
          {/* Left Side: Image + Bullets */}
          <div className={styles.leftSide}>
            <If condition={core.image}>
              <div className={styles.imageContainer}>
                <Image
                  src={core.image.url}
                  alt={core.image.description || core.title}
                  imgClassName={styles.image}
                  maxWidth="800"
                />
              </div>
            </If>

            <If condition={hasBullets}>
              {Array.isArray(core.bullets) ? (
                <ul className={styles.bullets}>
                  {core.bullets.map((bullet, idx) => (
                    <li key={`bullet-${idx}`}>{bullet}</li>
                  ))}
                </ul>
              ) : (
                <div className={styles.bulletsRichText}>
                  <RichText json={core.bullets?.json} />
                </div>
              )}
            </If>

            {/* Bottom: Product Data Sheets Accordion */}
            <If condition={pdfAccordionItems.length > 0}>
              <div className={styles.accordionSection}>
                <Accordion items={pdfAccordionItems} />
              </div>
            </If>
          </div>

          {/* Right Side: Specification Table */}
          <div className={styles.rightSide}>
            {core.description && (
              <RichText json={transformRichText(core.description.json)} />
            )}
            {/* <If condition={hasSpecificationTable}>
              <div className={styles.tableWrapper}>
                <table className={styles.specTable}>
                  <thead>
                    <tr>
                      {core.specificationTable.headers.map((header, idx) => (
                        <th key={`header-${idx}`}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {core.specificationTable.rows.map((row, rowIdx) => (
                      <tr key={`row-${rowIdx}`}>
                        <td className={styles.rowLabel}>{row.label}</td>
                        {row.values.map((value, colIdx) => (
                          <td key={`cell-${rowIdx}-${colIdx}`}>
                            {value.includes("\n")
                              ? value.split("\n").map((line, lineIdx) => (
                                  <React.Fragment key={`line-${lineIdx}`}>
                                    {lineIdx > 0 && <br />}
                                    {line}
                                  </React.Fragment>
                                ))
                              : value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </If> */}
          </div>
        </div>
      </div>
    </article>
  );
};

// Helper component for conditional rendering
const If: React.FC<{ condition: any; children: React.ReactNode }> = ({
  condition,
  children,
}) => (condition ? <>{children}</> : null);

export default CoreCard;
